// @flow
import React from 'react';
import InfoBox from '@justpark/ui/src/components/InfoBox';
import Typography from '@justpark/ui/src/components/Typography';
import InfoIcon from '@justpark/ui/src/components/Icons/InfoIcon';
import type { CheckoutSummaryResult } from '../../generated/graphql';

import styles from './checkoutSummary.module.scss';

type Props = {
  data: CheckoutSummaryResult,
  loading: boolean,
  error: null | {
    message: string
  },
  t: (string, string) => string,
  isMonthly: boolean
};

const CheckoutSummary = ({ data, loading, error, t, isMonthly }: Props) => {
  if (loading) {
    return <div className={styles.loadingPlaceholder} />;
  }

  if (data?.__typename === 'CheckoutSummary') {
    return (
      <InfoBox type="success" slim cypress="checkout-summary-price">
        <Typography className={styles.priceText}>
          {!isMonthly && (
            <span>
              <span className={styles.textStandout}>{`${t(
                'listingDetails:parkingFee',
                'Parking fee'
              )}:`}</span>{' '}
              {data.parkingFee.formatted}
            </span>
          )}
          {isMonthly && (
            <span>
              <span className={styles.textStandout}>{`${t(
                'listingDetails:amountPerMonth',
                'Amount per month'
              )}:`}</span>{' '}
              {data.breakdown[0].formatted}
            </span>
          )}
        </Typography>
      </InfoBox>
    );
  }

  if (data?.__typename === 'AvailabilityError') {
    return (
      <InfoBox
        type="blue"
        slim
        icon={<InfoIcon size="small" type="blue" />}
        className={styles.availabilityBox}
        cypress="checkout-summary-no-availability"
      >
        <Typography size="subtext">
          <span>
            {t(
              'listingDetails:availabilityError',
              'There is no availability at this date and time. Please try alternative dates, or find a different space nearby.'
            )}
          </span>
        </Typography>
      </InfoBox>
    );
  }

  if (data?.__typename === 'ValidationError' && Array.isArray(data.meta)) {
    return (
      <InfoBox type="error" slim cypress="checkout-summary-error">
        {data.meta.map(({ messages }) =>
          messages.map((message) => (
            <Typography size="subtext">
              <div>{message}</div>
            </Typography>
          ))
        )}
      </InfoBox>
    );
  }

  const generalError = data?.message || error?.message;

  if (generalError) {
    return (
      <InfoBox type="error" slim cypress="checkout-summary-error">
        <Typography size="subtext">
          <span>{generalError}</span>
        </Typography>
      </InfoBox>
    );
  }

  return null;
};

export default CheckoutSummary;
